import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import { getActivateCodeFromURL } from 'helpers/ActivateHelper';
import SignIn from './components/SignIn';
import PasswordCollection from './components/PasswordCollection';
import ForgotPassword from './components/ForgotPassword';
import { onSignin, onSignup, resetPassword } from './helpers/ConsolidatedHelper';

const LANDING_PAGE = 'landingPage';
const EXISTING_USER = 'existingUser';
const NEW_USER = 'newUser';
const FORGOT_PASSWORD = 'forgotPassword';

const ConsolidatedSignInFlow = props => {
  const {
    anonymousAccount,
    handleActivateCode,
    headerProps,
    language,
    login,
    register,
    reset,
    signInCompleted,
    signInSuccess,
    signUpCompleted,
    signupSuccess,
    toggleSignupSuccess,
    utagAccountSource,
  } = props;
  const SCREENS = {
    [LANDING_PAGE]: SignIn,
    [EXISTING_USER]: PasswordCollection,
    [NEW_USER]: PasswordCollection,
    [FORGOT_PASSWORD]: ForgotPassword,
  };
  const [screen, setScreen] = useState({
    prev: LANDING_PAGE,
    current: LANDING_PAGE,
  });
  const { legalTermsUrl, legalTermsName, legalPrivacyUrl, legalPrivacyName } = language;

  const [email, setEmail] = useState('');
  const [existingEmail, setExistingEmail] = useState(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const code = getActivateCodeFromURL() ?? '';
  const screenProps = {
    [LANDING_PAGE]: {},
    [EXISTING_USER]: {
      buttonText: 'Sign In',
      headline: 'Welcome Back!',
      disclosure: null,
      forgotPasswordText: 'Forgot Your Password?',
      onSubmit: props =>
        onSignin({
          ...props,
          email,
          handleActivateCode,
          setIsProcessing,
          headerProps,
          login,
          signInCompleted,
          signInSuccess,
          utagAccountSource,
        }),
      onResetPassword: () => {
        resetPassword({
          reset,
          email,
          setScreen,
          forgotPasswordPage: FORGOT_PASSWORD,
          existingUserPage: EXISTING_USER,
        });
        handleAnalytics({
          event: Events.PROFILE_SIGN_IN_FORGOT_PASSWORD,
          payload: { page_start_source: utagAccountSource },
        });
      },
      onLoad: () => {
        handleAnalytics({
          event: Events.PROFILE_SIGN_IN_STARTED,
          payload: {
            page_start_source: utagAccountSource,
            page_profile_activation_method: code && '2nd screen',
            page_activation_code: code,
          },
        });
      },
    },
    [NEW_USER]: {
      buttonText: 'Create Account',
      headline: 'Setup your Password',
      disclosure: `By clicking "Create Account”, you agree to the <a href="${legalTermsUrl}" target="_blank" rel="noopener noreferrer" >${legalTermsName}</a>, <a href="${legalPrivacyUrl}" target="_blank" rel="noopener noreferrer">${legalPrivacyName}</a>, and agree to receive content and promotional communications from FOX.`,
      onSubmit: props =>
        onSignup({
          ...props,
          register,
          setIsProcessing,
          signUpCompleted,
          toggleSignupSuccess,
          email,
          utagAccountSource,
          handleActivateCode,
          language,
          anonymousAccount,
          signupSuccess,
        }),
      onLoad: () => {
        handleAnalytics({
          event: Events.PROFILE_SIGNUP_STATRED,
          payload: {
            page_start_source: utagAccountSource,
            page_profile_activation_method: code && '2nd screen',
            page_activation_code: code,
          },
        });
      },
    },
  };
  useEffect(() => {
    if (existingEmail !== undefined) {
      if (existingEmail && email) {
        setScreen({ current: EXISTING_USER, prev: LANDING_PAGE });
      } else if (email && !existingEmail) {
        setScreen({ current: NEW_USER, prev: LANDING_PAGE });
      }
    }
  }, [existingEmail, email]);

  const Component = SCREENS[screen.current];
  if (!Component) return null;
  return (
    <Component
      {...props}
      email={email}
      existingUserPage={EXISTING_USER}
      isProcessing={isProcessing}
      landingPage={LANDING_PAGE}
      language={language}
      previous={screen.prev}
      screenProps={screenProps[screen.current]}
      setEmail={setEmail}
      setExistingEmail={setExistingEmail}
      setScreen={data => setScreen(data)}
    />
  );
};

export default ConsolidatedSignInFlow;

ConsolidatedSignInFlow.propTypes = {
  anonymousAccount: PropTypes.shape({}),
  handleActivateCode: PropTypes.func,
  headerProps: PropTypes.shape({}),
  language: PropTypes.shape({}).isRequired,
  login: PropTypes.func,
  register: PropTypes.func,
  reset: PropTypes.func,
  signInCompleted: PropTypes.func,
  signInSuccess: PropTypes.func,
  signUpCompleted: PropTypes.func,
  signupSuccess: PropTypes.func,
  toggleSignupSuccess: PropTypes.func,
  utagAccountSource: PropTypes.string,
};
